import * as React from 'react';

import type { AjaxError } from 'rxjs';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { OPERATIONAL_EVENT_TYPE } from '@atlassian/analytics-web-react';
import { RESOURCE_TYPE_ISSUE, RESOURCE_TYPE_PROJECT } from '@atlassian/help-center-common-component/forge-ui/constants';
import { trackError } from '@atlassian/help-center-common-util/analytics';

import { getMetaFromHTML } from '@atlassian/help-center-common-util/meta';
import { TFECacheContext } from '../tfe-cache-context';
import { AtlassianHelpConfigAccountType } from '../types';
import type { Extension, ExtensionPointModule, ForgePageName, ResourceContext } from '../types';

export const useTFExtensionList = ({
    type,
    resourceContext,
    page,
}: {
    type: ExtensionPointModule;
    resourceContext?: ResourceContext;
    page?: ForgePageName;
}) => {
    const [extensions, setExtensions] = React.useState<Extension[]>([]);
    const accountType = getMetaFromHTML('ajs-account-type') || AtlassianHelpConfigAccountType.ANONYMOUS;
    const { getExtensionsByType, getExtensionsByTypeV2 } = React.useContext(TFECacheContext);
    const { createAnalyticsEvent } = useAnalyticsEvents();
    React.useEffect(() => {
        if (
            accountType === AtlassianHelpConfigAccountType.ATLASSIAN ||
            accountType === AtlassianHelpConfigAccountType.ANONYMOUS ||
            accountType === AtlassianHelpConfigAccountType.CUSTOMER
        ) {
            const { resourceType } = resourceContext || {};
            if (resourceType === RESOURCE_TYPE_ISSUE || resourceType === RESOURCE_TYPE_PROJECT) {
                getExtensionsByTypeV2(type, resourceContext, page)
                    .then((fetchedExtensions) => {
                        setExtensions(fetchedExtensions[type]);
                    })
                    .catch((e: AjaxError) => {
                        trackError('forge-tfe.setState.failed', {}, e);
                    });
            } else {
                getExtensionsByType(type, page)
                    .then((fetchedExtensions) => {
                        setExtensions(fetchedExtensions[type]);
                    })
                    .catch((e: AjaxError) => {
                        trackError('forge-tfe.setState.failed', {}, e);
                    });
            }
        }
    }, [accountType, getExtensionsByType, getExtensionsByTypeV2, type, resourceContext, page, createAnalyticsEvent]);

    React.useEffect(() => {
        extensions.forEach((extension) => {
            const { id, dataClassificationPolicyDecision } = extension;
            const event = createAnalyticsEvent({
                actionSubject: 'forgeTFE',
                action: 'fetched',
                analyticsType: OPERATIONAL_EVENT_TYPE,
                attributes: {
                    page,
                    moduleType: type,
                    status: dataClassificationPolicyDecision?.status,
                    extensionId: id,
                },
            });
            event.context.push({ componentName: 'forgeTFE' });
            event.fire();
        });
    }, [extensions, page, createAnalyticsEvent, type]);

    return {
        extensions,
    };
};
